import {
  Wrapper,
  TableTitle,
  TableWrapper,
  AddNewRowBtn,
  RemoveRowBtn,
  EditComment,
} from './styles';
import { useMemo, useState, useEffect } from 'react';
import { useTable } from 'react-table';
import EditableCell from '../EditableCell';
import { EditTitle } from '../ViewsAndLayoutsEdit/ViewsAndLayoutsListItem/styles';

const tableHeaders = [
  {
    Header: 'Main tie-ins list',
    columns: [
      {
        Header: 'N°',
        columns: [
          { Header: 'N° INVISIBLE', displayNone: true, accessor: 'nr' },
        ],
        rowSpan: 2,
      },
      {
        Header: 'Description',
        columns: [
          {
            Header: 'Description INVISIBLE',
            displayNone: true,
            accessor: 'description',
          },
        ],
        rowSpan: 2,
      },
    ],
  },
  {
    Header: 'Tie-ins details',
    columns: [
      {
        Header: 'Ø',
        columns: [
          {
            Header: 'Inch',
            accessor: 'diameter',
          },
        ],
      },
      {
        Header: 'Rating',
        columns: [
          {
            Header: '#',
            accessor: 'rating',
          },
        ],
      },
      {
        Header: 'Material',
        columns: [
          {
            Header: 'Material INVISIBLE',
            displayNone: true,
            accessor: 'material',
          },
        ],
        rowSpan: 2,
      },
    ],
  },
  {
    Header: 'Tie-in point coordinates',
    columns: [
      {
        Header: 'Tie-in Point N°',
        columns: [
          {
            Header: 'Tie-in INVISIBLE',
            displayNone: true,
            accessor: 'pointNo',
          },
        ],
        rowSpan: 2,
      },
      {
        Header: 'X',
        columns: [{ Header: 'm', colSpan: '3', accessor: 'x' }],
      },
      {
        Header: 'Y',
        columns: [
          { Header: 'm INVISIBLE 1', displayNone: true, accessor: 'y' },
        ],
      },
      {
        Header: 'Z',
        columns: [
          { Header: 'm INVISIBLE 2', displayNone: true, accessor: 'z' },
        ],
      },
    ],
  },
  {
    Header: () => null,
    id: 'remove_Row_Btn',
    Cell: ({ row: { index }, deleteRow }) => (
      <RemoveRowBtn
        onClick={() => {
          deleteRow(index);
        }}
      />
    ),
  },
];

const defaultColumn = {
  Cell: EditableCell,
};

const MainTieInsEdit = ({
  title,
  tableData,
  setEditedDataField,
  tieInsOtherUtilities,
  tieInsOtherNetworks,
}) => {
  const [skipPageReset, setSkipPageReset] = useState(false);
  const columns = useMemo(() => tableHeaders, []);

  const Table = ({
    columns,
    data,
    updateCellData,
    skipPageReset,
    editFieldProps,
    deleteRow,
  }) => {
    const tableInstance = useTable({
      columns,
      data,
      defaultColumn,
      // use the skipPageReset option to disable page resetting temporarily
      autoResetPage: !skipPageReset,
      // updateCellData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateCellData,
      editFieldProps,
      deleteRow,
    });

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
      tableInstance;

    return (
      <table {...getTableProps()}>
        <colgroup>
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
          <col />
        </colgroup>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps()}
                  rowSpan={`${column.rowSpan ?? 1}`}
                  colSpan={`${
                    column.colSpan ?? column.getHeaderProps().colSpan
                  }`}
                  style={column.displayNone ? { display: 'none' } : {}}
                >
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, i) => {
            Object.keys(row.values).forEach((valueKey) => {
              row.values[valueKey] = row.values[valueKey] ?? '';
            });
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // When our cell renderer calls updateCell, we'll use
  // the rowIndex, columnId and new value to update the
  // original data
  const updateCellData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    let currentData = tableData.map((row, index) => {
      if (index === rowIndex) {
        return {
          ...tableData[rowIndex],
          [columnId]: value,
        };
      }
      return row;
    });
    setEditedDataField('tieIns', currentData);
  };

  const deleteRow = (rowIndex) => {
    setSkipPageReset(true);
    let currentData = [...tableData];
    currentData.splice(rowIndex, 1);
    setEditedDataField('tieIns', currentData);
  };

  const addNewRow = () => {
    let currentData = [...tableData];
    currentData.push({
      number: '',
      description: '',
      theta: '',
      rating: '',
      material: '',
      tieInPoint: '',
      x: '',
      y: '',
      z: '',
    });
    setEditedDataField('tieIns', currentData);
  };

  const updateTieInsOtherUtilities = (e) => {
    const newVal = e.target.value;
    setEditedDataField('tieInsOtherUtilities', newVal);
  };

  const updateTieInsOtherNetworks = (e) => {
    const newVal = e.target.value;
    setEditedDataField('tieInsOtherNetworks', newVal);
  };

  // After data chagnes, we turn the flag back off
  // so that if data actually changes when we're not
  // editing it, the page is reset
  useEffect(() => {
    setSkipPageReset(false);
  }, [tableData]);

  return (
    <Wrapper>
      <TableTitle
        placeholder='Main Tie Ins Comment...'
        value={title ?? ''}
        onChange={(e) => {
          setEditedDataField(e.target.name, e.target.value);
        }}
        name='tieInsComment'
        type='text'
      />
      <TableWrapper>
        <Table
          columns={columns}
          data={tableData}
          updateCellData={updateCellData}
          skipPageReset={skipPageReset}
          deleteRow={deleteRow}
          editFieldProps={{
            number: {
              type: 'number',
            },
            theta: {
              type: 'number',
            },
            x: {
              type: 'number',
            },
            y: {
              type: 'number',
            },
            z: {
              type: 'number',
            },
          }}
        />
      </TableWrapper>
      <AddNewRowBtn title={'add row'} onClick={addNewRow} />
      <table>
        <thead>
          <tr>
            <th style={{ borderTop: 'none' }}>
              Module tie-ins to other utilities
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <EditTitle
                value={tieInsOtherUtilities ?? ''}
                name={'tieInsOtherUtilities'}
                placeholder=''
                onChange={updateTieInsOtherUtilities}
                type='text'
              />
            </td>
          </tr>
        </tbody>
      </table>
      <table>
        <thead>
          <tr>
            <th style={{ borderTop: 'none' }}>
              Under deck tie-ins to other networks
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <EditTitle
                value={tieInsOtherNetworks ?? ''}
                name={'tieInsOtherNetworks'}
                placeholder=''
                onChange={updateTieInsOtherNetworks}
                type='text'
              />
            </td>
          </tr>
        </tbody>
      </table>
    </Wrapper>
  );
};

export default MainTieInsEdit;
